import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axiosInstance from '../../axiosConfig';
import Notification from '../notification/Notification';
import '../../styles/Auth.css';
import { Circles } from 'react-loader-spinner';
import OAuth from './OAuth';

const Signup = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [notification, setNotification] = useState(null);
    const [loading, setLoading] = useState(false);
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        const referralCode = new URLSearchParams(location.search).get('referralCode');
        if (referralCode) {
            localStorage.setItem('referralCode', referralCode);
        }
    }, [location.search]);

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.toLowerCase());

    const validatePassword = (password) => {
        const minLength = 4;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumber = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        if (password.length < minLength) {
            return { valid: false, message: 'Password must be at least 4 characters long.' };
        }
        if (!hasUpperCase) {
            return { valid: false, message: 'Password must include at least one uppercase letter.' };
        }
        if (!hasLowerCase) {
            return { valid: false, message: 'Password must include at least one lowercase letter.' };
        }
        if (!hasNumber) {
            return { valid: false, message: 'Password must include at least one number.' };
        }
        if (!hasSpecialChar) {
            return { valid: false, message: 'Password must include at least one special character.' };
        }
        return { valid: true, message: '' };
    };

    const handleSignup = async (e) => {
        e.preventDefault();

        if (!email || !password) {
            setNotification({ message: 'Please fill out all fields.', isError: true });
            return;
        }

        if (!validateEmail(email)) {
            setNotification({ message: 'Please enter a valid email address.', isError: true });
            return;
        }

        const passwordValidation = validatePassword(password);
        if (!passwordValidation.valid) {
            setNotification({ message: passwordValidation.message, isError: true });
            return;
        }

        setLoading(true);

        try {
            const referralCode = localStorage.getItem('referralCode');
            const response = await axiosInstance.post('/api/auth/register', { email, password, referralCode });

            const { accessToken, refreshToken, invitationLink, user } = response.data;
            login({ email: user.email, id: user.id, isAdmin: user.isAdmin }, accessToken, refreshToken, invitationLink);

            localStorage.removeItem('referralCode');
            navigate('/');
        } catch (error) {
            if (error.response) {
                const { status, data } = error.response;
                let errorMessage = 'An error occurred. Please try again later.';

                if (status === 409) {
                    errorMessage = 'A user with this email already exists.';
                } else if (status === 400 && data.message) {
                    errorMessage = data.message;
                }

                setNotification({ message: errorMessage, isError: true });
            } else {
                setNotification({ message: 'An error occurred. Please try again later.', isError: true });
                // console.error('Signup error:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
        const passwordField = document.getElementById('password');
        passwordField.type = showPassword ? 'password' : 'text';
    };

    return (
        <div className="auth-container">
            <h2>Sign Up</h2>
            <form onSubmit={handleSignup}>
                <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                />

                <div className='show-hide-password'>
                    <input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                        id="password"
                    />

                    <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="show-password-button"
                    >
                        {showPassword ? 'Hide' : 'Show'}
                    </button>
                </div>

                <div className="notification-container">
                    <button type="submit" disabled={loading} className="auth-button">
                        {loading ? (
                            <Circles
                                height="20"
                                width="20"
                                color="#ffffff"
                                ariaLabel="circles-loading"
                                wrapperStyle={{ display: 'flex', justifyContent: 'center' }}
                                visible={true}
                            />
                        ) : (
                            'Sign Up'
                        )}
                    </button>
                    {notification && (
                        <Notification
                            message={notification.message}
                            isError={notification.isError}
                            onClose={() => setNotification(null)}
                        />
                    )}
                </div>
            </form>
            <div className="auth-footer">
                <Link to="/login" className="auth-link">
                    Sign in?
                </Link>
            </div>
            <OAuth /> {/* Add the OAuth component here */}
        </div>
    );
};

export default Signup;
